import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Check, Building2,
  Globe, HelpCircle, ChevronDown,
  TrendingUp, BarChart3, DollarSign, Monitor
} from 'lucide-react';
import { Logo } from '../common';
import Footer from '../common/Footer';
import StripeCheckoutButton from '../stripe/StripeCheckoutButton';
import useMetaTags from '../../hooks/useMetaTags';

const PricingPage = () => {
  useMetaTags({
    title: 'ForwardsFlow | Pricing',
    description: 'Enterprise treasury cashflow management pricing. Allocation-based tiers from Explorer to Institutional, with transparent FX fees and forward pooling.',
    canonical: 'https://www.forwardsflow.com/pricing',
  });
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('treasurer');

  const planTypeMap = {
    'treasurer_Explorer': null,
    'treasurer_Professional': 'investor_starter',
    'treasurer_Enterprise': 'investor_growth',
    'treasurer_Institutional': 'investor_premium',
    'bank_Platform Access': 'bank_platform_access',
  };

  const treasurerTiers = [
    {
      name: 'Explorer',
      price: 0,
      allocationSize: 'Under $50k',
      description: 'Discover yields and benchmark returns across frontier fixed income markets.',
      badge: null,
      features: [
        'Yield discovery across 6 markets',
        'Portfolio dashboard',
        'Basic FX conversion',
        'T-bill auction access',
        'Email support',
      ],
      cta: 'Get Started',
      popular: false,
    },
    {
      name: 'Professional',
      price: 49,
      allocationSize: '$50k \u2013 $500k',
      description: 'Execute, hedge, and manage treasury allocations across multiple markets.',
      badge: null,
      features: [
        'All Explorer features',
        'Multi-market execution',
        'FX hedging overlays',
        'Forward pooling eligible',
        'Automated rollover',
        'Priority support',
      ],
      cta: 'Get Started',
      popular: false,
    },
    {
      name: 'Enterprise',
      price: 249,
      allocationSize: '$500k \u2013 $5M',
      description: 'Full-stack treasury management with dedicated coverage and advanced analytics.',
      badge: 'MOST POPULAR',
      features: [
        'All Professional features',
        'All 6 markets access',
        'Forward pooling priority allocation',
        'Dedicated account manager',
        'Advanced risk & performance analytics',
        'Custom compliance workflows',
      ],
      cta: 'Get Started',
      popular: true,
    },
    {
      name: 'Institutional',
      price: null,
      allocationSize: 'Above $5M',
      description: 'Custom terms, API integration, and white-glove treasury operations.',
      badge: null,
      features: [
        'All Enterprise features',
        'Primary auction direct access',
        'Custom FX terms & negotiated spreads',
        'Full API access & webhooks',
        'White-glove onboarding & migration',
        'Dedicated SLA & 24/7 support',
      ],
      cta: 'Contact Sales',
      popular: false,
    },
  ];

  const fxFees = [
    { range: 'Under $100k', fee: '25 bps' },
    { range: '$100k \u2013 $1M', fee: '15 bps' },
    { range: '$1M+', fee: '8 bps (negotiable)' },
  ];

  const additionalFees = [
    { item: 'Forward pooling participation', fee: '10 bps on notional' },
    { item: 'T-bill auction participation', fee: 'Included' },
    { item: 'Secondary market transactions', fee: 'Included' },
  ];

  const bankFees = [
    { item: 'Monthly platform access', detail: '$2,500 per bank, flat fee' },
    { item: 'Dealer seats beyond first two', detail: '$500 / seat / month' },
    { item: 'Account origination', detail: '$25 per onboarded, funded account' },
  ];

  const faqs = [
    {
      q: 'How does allocation-based pricing work?',
      a: 'Your monthly subscription is determined by your total treasury allocation on ForwardsFlow. As your managed cashflows grow into the next tier, your subscription adjusts at the next billing cycle. There are no lock-ups or long-term commitments \u2014 you can adjust or cancel anytime.'
    },
    {
      q: 'What are foreign exchange fees and how are they charged?',
      a: 'FX fees are charged as basis points on each currency conversion when deploying capital into local-currency instruments. Larger allocations unlock better rates: 25 bps under $100k, 15 bps for $100k\u2013$1M, and 8 bps (negotiable) for allocations above $1M. FX fees are applied at the point of conversion only.'
    },
    {
      q: 'What is forward pooling?',
      a: 'Forward pooling allows treasurers to participate in aggregated FX forward contracts, reducing the per-unit cost of hedging frontier currency exposure. Participation costs 10 bps on the notional value of the forward. Available on Professional tier and above.'
    },
    {
      q: 'How does T-bill auction participation work?',
      a: 'ForwardsFlow provides access to primary T-bill auctions across frontier markets. Auction participation is included at no additional cost on all tiers. You can bid on new issuances directly through the platform with settlement via the local central securities depository.'
    },
    {
      q: 'What payment methods do you accept?',
      a: 'We accept wire transfers in USD, EUR, and GBP. Funds are held in segregated client accounts with counterparty banks. Institutional clients can arrange custom funding and settlement arrangements.'
    },
    {
      q: 'How does bank partner pricing work?',
      a: 'Banks pay a flat monthly platform access fee of $2,500. The first two dealer seats are included; additional seats are $500/seat/month. Account origination is charged at $25 per onboarded, funded account. There are no volume-based overages or hidden fees.'
    },
    {
      q: 'Can I change my tier at any time?',
      a: 'Yes. All subscriptions are month-to-month with no minimum commitment. Upgrades take effect immediately; adjustments apply at the next billing cycle. Your tier is based on your treasury allocation, so it adjusts naturally as your managed cashflows change.'
    },
    {
      q: 'What does the Institutional tier include?',
      a: 'Institutional pricing is customised for treasuries managing over $5M. It includes negotiated FX spreads, full API access with webhooks, dedicated SLA, white-glove onboarding, and a named account manager. Contact our sales team to discuss terms.'
    },
  ];

  const [expandedFaq, setExpandedFaq] = useState(null);

  return (
    <div className="min-h-screen bg-white text-black font-sans antialiased">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-20">
            <Logo />
            <div className="hidden lg:flex items-center gap-6">
              <Link to="/" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Home</Link>
              <Link to="/pricing" className="font-mono text-xs uppercase tracking-[0.15em] text-black font-bold border-b-2 border-black pb-1">Pricing</Link>
              <Link to="/open-account" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Open Account</Link>
              <Link to="/demo" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Book a Demo</Link>
              <Link to="/contact" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Contact</Link>
              <Link to="/login" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-800 hover:text-black font-medium">Platform Login</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Header Banner */}
      <div className="pt-32 pb-16 bg-[#4D4D4D] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-zinc-300 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>
          <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-300 block mb-2">Commercial Terms</span>
          <h1 className="font-sans text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">Simple, transparent pricing</h1>
          <p className="font-sans text-base text-zinc-300 max-w-2xl">
            Allocation-based subscriptions aligned with your treasury size. No performance fees.<br />No lock-ups. Transparent FX and pooling costs.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 -mt-6">
        <div className="bg-white border border-zinc-300 p-2 inline-flex gap-2 rounded-none">
          {[
            { id: 'treasurer', label: 'Treasurers', icon: TrendingUp },
            { id: 'bank', label: 'Banks', icon: Building2 }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] font-medium rounded-none transition ${
                activeTab === tab.id
                  ? 'bg-[#4D4D4D] text-white'
                  : 'text-zinc-600 hover:bg-zinc-100 hover:text-black'
              }`}
            >
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Pricing Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">

        {/* Treasurer Tiers */}
        {activeTab === 'treasurer' && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
              {treasurerTiers.map((tier, idx) => (
                <div
                  key={idx}
                  className={`bg-white border p-8 rounded-none relative transition-all flex flex-col h-full ${
                    tier.popular ? 'border-[#4D4D4D]' : 'border-zinc-300'
                  }`}
                >
                  {tier.badge && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#4D4D4D] text-white font-mono text-[10px] uppercase tracking-[0.15em] px-3 py-1 border border-[#4D4D4D] rounded-none">
                      {tier.badge}
                    </span>
                  )}

                  <div>
                    <h3 className="font-sans text-xl font-bold text-black mt-2">{tier.name}</h3>
                    <p className="font-mono text-xs text-zinc-500 mt-1">{tier.allocationSize} allocation</p>
                    <p className="text-xs font-sans text-zinc-500 mt-2">{tier.description}</p>

                    <div className="mt-4 mb-6">
                      <span className="font-sans text-4xl font-bold text-black">
                        {tier.price === 0 ? 'Free' : tier.price === null ? 'Custom' : `$${tier.price}`}
                      </span>
                      {tier.price > 0 && (
                        <span className="text-zinc-500 font-mono text-xs">/month</span>
                      )}
                    </div>

                    <ul className="space-y-3 mb-8">
                      {tier.features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-start gap-2 text-xs font-sans">
                          <Check className="w-4 h-4 text-black flex-shrink-0 mt-0.5" />
                          <span className="text-zinc-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-auto pt-4 border-t border-zinc-100">
                    <StripeCheckoutButton
                      planType={planTypeMap[`treasurer_${tier.name}`]}
                      billingInterval="monthly"
                      label={tier.cta}
                      isEnterprise={tier.cta === 'Contact Sales'}
                      popular={tier.popular}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* FX Fees Section */}
            <div className="mt-12 bg-white border border-[#4D4D4D] p-8 rounded-none">
              <h3 className="font-sans text-xl font-bold text-black mb-6">FX Conversion Fees</h3>
              <p className="text-xs font-sans text-zinc-600 mb-6 leading-relaxed">
                FX fees are charged as basis points on each currency conversion when deploying capital into local-currency instruments. Rates are tiered by total treasury allocation.
              </p>
              <table className="w-full text-xs font-sans">
                <thead>
                  <tr className="border-b border-zinc-300">
                    <th className="py-3 text-left font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium">Treasury Allocation</th>
                    <th className="py-3 text-right font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium">FX Fee</th>
                  </tr>
                </thead>
                <tbody>
                  {fxFees.map((row, idx) => (
                    <tr key={idx} className={idx < fxFees.length - 1 ? 'border-b border-zinc-200' : ''}>
                      <td className="py-3 text-zinc-700">{row.range}</td>
                      <td className="py-3 text-right font-mono font-bold text-black">{row.fee}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Additional Fees Section */}
            <div className="mt-6 bg-white border border-zinc-300 p-8 rounded-none">
              <h3 className="font-sans text-xl font-bold text-black mb-6">Additional Fees</h3>
              <table className="w-full text-xs font-sans">
                <thead>
                  <tr className="border-b border-zinc-300">
                    <th className="py-3 text-left font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium">Item</th>
                    <th className="py-3 text-right font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium">Fee</th>
                  </tr>
                </thead>
                <tbody>
                  {additionalFees.map((row, idx) => (
                    <tr key={idx} className={idx < additionalFees.length - 1 ? 'border-b border-zinc-200' : ''}>
                      <td className="py-3 text-zinc-700">{row.item}</td>
                      <td className="py-3 text-right font-mono font-bold text-black">{row.fee}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* How Our Fees Work */}
            <div className="mt-12 bg-white border border-[#4D4D4D] p-8 rounded-none">
              <h3 className="font-sans text-xl font-bold text-black mb-6">How our fees work</h3>
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <h4 className="font-mono text-xs uppercase tracking-[0.15em] mb-4 flex items-center gap-2 text-black font-bold">
                    <BarChart3 className="w-4 h-4 text-black" /> Allocation-Based Subscription
                  </h4>
                  <p className="text-xs font-sans text-zinc-600 leading-relaxed">
                    Your monthly subscription is based on your total treasury allocation on ForwardsFlow. Start free with allocations under $50k and scale as your managed cashflows grow. No performance fees, no carried interest, no hidden charges.
                  </p>
                </div>
                <div>
                  <h4 className="font-mono text-xs uppercase tracking-[0.15em] mb-4 flex items-center gap-2 text-black font-bold">
                    <Globe className="w-4 h-4 text-black" /> FX Conversion
                  </h4>
                  <p className="text-xs font-sans text-zinc-600 leading-relaxed">
                    FX fees are charged in basis points when converting between your base currency (USD/EUR/GBP) and the local currency of the target instrument. Larger allocations unlock better rates, from 25 bps down to 8 bps (negotiable at institutional scale).
                  </p>
                </div>
                <div>
                  <h4 className="font-mono text-xs uppercase tracking-[0.15em] mb-4 flex items-center gap-2 text-black font-bold">
                    <TrendingUp className="w-4 h-4 text-black" /> Forward Pooling
                  </h4>
                  <p className="text-xs font-sans text-zinc-600 leading-relaxed">
                    Participate in aggregated FX forward pools to reduce hedging costs on frontier currency exposure. Pooling participation is 10 bps on notional value. T-bill auction access and secondary market transactions are included on all tiers at no extra cost.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Bank Pricing */}
        {activeTab === 'bank' && (
          <>
            <div className="max-w-2xl mx-auto">
              <div className="bg-white border border-[#4D4D4D] p-8 rounded-none">
                <h3 className="font-sans text-xl font-bold text-black mb-2">Bank Platform Access</h3>
                <p className="text-xs font-sans text-zinc-500 mb-6">Flat, predictable pricing for bank partners. No volume-based overages or hidden fees.</p>

                <table className="w-full text-sm font-sans">
                  <thead>
                    <tr className="border-b border-zinc-300">
                      <th className="py-3 text-left font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium">Item</th>
                      <th className="py-3 text-right font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bankFees.map((row, idx) => (
                      <tr key={idx} className={idx < bankFees.length - 1 ? 'border-b border-zinc-200' : ''}>
                        <td className="py-4 text-zinc-700 text-xs">{row.item}</td>
                        <td className="py-4 text-right font-mono font-bold text-black text-xs">{row.detail}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="mt-8 pt-6 border-t border-zinc-100 flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/contact"
                    className="block w-full py-3 text-center font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] bg-[#4D4D4D] text-white hover:bg-black rounded-none transition"
                  >
                    Contact Sales
                  </Link>
                  <Link
                    to="/demo"
                    className="block w-full py-3 text-center font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] text-[#4D4D4D] hover:bg-[#4D4D4D] hover:text-white rounded-none transition"
                  >
                    Book a Demo
                  </Link>
                </div>
              </div>
            </div>

            {/* Bank Partnership Details */}
            <div className="mt-12 bg-white border border-zinc-300 p-8 rounded-none">
              <h3 className="font-sans text-xl font-bold text-black mb-6">Bank Partnership Details</h3>
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <h4 className="font-mono text-xs uppercase tracking-[0.15em] mb-4 flex items-center gap-2 text-black font-bold">
                    <DollarSign className="w-4 h-4 text-black" /> Flat Monthly Fee
                  </h4>
                  <p className="text-xs font-sans text-zinc-600 leading-relaxed">
                    A flat $2,500 monthly fee covers full platform access, API integration, compliance onboarding, and two dealer seats. No volume-based overages or transaction commissions.
                  </p>
                </div>
                <div>
                  <h4 className="font-mono text-xs uppercase tracking-[0.15em] mb-4 flex items-center gap-2 text-black font-bold">
                    <Monitor className="w-4 h-4 text-black" /> Dealer Seats
                  </h4>
                  <p className="text-xs font-sans text-zinc-600 leading-relaxed">
                    The first two dealer seats are included with your monthly platform fee. Additional seats for your FX desk or operations team are available at $500 per seat per month, billed monthly.
                  </p>
                </div>
                <div>
                  <h4 className="font-mono text-xs uppercase tracking-[0.15em] mb-4 flex items-center gap-2 text-black font-bold">
                    <TrendingUp className="w-4 h-4 text-black" /> Account Origination
                  </h4>
                  <p className="text-xs font-sans text-zinc-600 leading-relaxed">
                    Each new treasury account originated and funded through your bank partnership is charged a one-time $25 onboarding fee. This covers KYC/AML verification, depository account setup, and initial compliance review.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* FAQ Section */}
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pb-20">
        <h2 className="font-sans text-2xl font-bold text-center text-black mb-8">Frequently asked questions</h2>
        <div className="space-y-3">
          {faqs.map((faq, idx) => (
            <div key={idx} className="bg-white border border-zinc-300 rounded-none">
              <button
                onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                className="w-full flex items-center justify-between p-4 text-left"
              >
                <span className="font-sans text-sm font-medium text-black flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-zinc-500" /> {faq.q}
                </span>
                <ChevronDown className={`w-4 h-4 text-zinc-500 transition ${expandedFaq === idx ? 'rotate-180' : ''}`} />
              </button>
              {expandedFaq === idx && (
                <div className="px-4 pb-4 text-xs font-sans text-zinc-600 leading-relaxed border-t border-zinc-100 pt-3">{faq.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      <Footer
        ctaHeading="FX and forwards contract deal flow."
        ctaDescription="FX and transaction deal flow from cross-border fixed income treasury activity."
      />
    </div>
  );
};

export default PricingPage;
